import * as s from "styles/ModalCommonStyled";
import BigPopupTemplate from "components/templates/modal/big/BigPopupTemplate";
import BigButton from "components/atoms/buttons/BigButton";
import { ModalInput } from "components/atoms/input/modal/ModalInput";
import Input from "components/atoms/input/Input";
import { Text, ValidateWord } from "components/atoms/text/Text";
import { BACKGROUND_COLORS } from "styles/colors";
import { MessageInput, NumberInput } from "../NoticeStyled";
import { ICON_TYPE, Notice } from "api/interfaces/noticeInterface.interface";
import { ValidateMsg } from "api/interfaces/commonInterface.interface";
import { PiWarningCircleBold } from "react-icons/pi";
import { PiWarningBold } from "react-icons/pi";
import { IoDocumentTextOutline } from "react-icons/io5";
import NoticeEditor from "components/atoms/input/editor/NoticeEditor";
import { extractTextFromHTML } from "utils/functions";
import ReactQuill, { UnprivilegedEditor } from "react-quill";
import { useMemo, useState } from "react";
import { DeltaStatic, Sources } from "quill";

interface Props {
  isEdit?: boolean;
  onModal: (type?: string) => void;
  onChange: (e: any) => void;
  notice: Notice;
  onDelete: () => void;
  validateMsg?: ValidateMsg;
  isPermanently: boolean;
  onChangePermanent: (e: any) => void;
  onSave: () => void;
}

/**
 * @refactor
 * @todo ReactNode와 string을 함께 받으면 불필요한 타입체크가 많이 필요함 as string
 * @todo 프레젠터에서 표시 할 타입만 enum으로 정의해서 export하고, 아이콘에 대한 핸들링은 Presenter
 * @todo 컨테이너는 타입이 중요하지 어떤 아이콘을 표시할지는 Presente에서 고민하는게 어떨까요?
 */
export function AddModalPresenter(props: Props): JSX.Element {
  const messageIcons = [
    {
      label: "None",
      value: ICON_TYPE.NONE,
    },
    {
      // label: <img src={WARNING_CIRCLE_ICON} />,
      value: ICON_TYPE.INFO,
      label: <PiWarningCircleBold size={20} />,
    },
    {
      // label: <img src={WARNING_TRIANGLE_ICON} />,
      value: ICON_TYPE.WARNING,
      label: <PiWarningBold size={20} />,
    },
    {
      // label: <img src={DOCUMENT_ICON} />,
      value: ICON_TYPE.DOCUMENT,
      label: <IoDocumentTextOutline size={20} />,
    },
  ];

  const bgColorIcons = BACKGROUND_COLORS.map((color) => {
    return {
      label: Object.keys(color)[0],
      value: Object.values(color)[0],
    };
  });

  const isDisabledInput = useMemo(() => {
    // console.log(extractTextFromHTML(props.notice.message).length > 3);
    return extractTextFromHTML(props.notice.message).length > 3;
  }, [props.notice.message]);

  return (
    <BigPopupTemplate
      title={props.isEdit ? "Edit Notice" : "Add Notice"}
      onModal={props.onModal}
    >
      <s.AddWrapper>
        <ModalInput label="Notice Title" essential>
          <Input
            placeholder="Please enter a title"
            name="subject"
            onChange={props.onChange}
            value={props.notice.subject}
            data-testid="manager-site-notices-add-subject-change"
          />
        </ModalInput>
        <s.ValidateWrapper>
          <ValidateWord data-testid="manager-site-notices-add-validate-title">
            {props.validateMsg?.title}
          </ValidateWord>
        </s.ValidateWrapper>
        <ModalInput label="Message" essential>
          <NoticeEditor
            isDisabledInput={isDisabledInput}
            value={props.notice.message}
            onChange={(
              value: string,
              delta: DeltaStatic,
              source: Sources,
              editor: UnprivilegedEditor
            ) => {
              props.onChange({ target: { name: "message", value } });
            }}
          />
        </ModalInput>
        <s.ValidateWrapper>
          <ValidateWord data-testid="manager-site-notices-add-validate-message">
            {props.validateMsg?.message}
          </ValidateWord>
        </s.ValidateWrapper>
        <ModalInput label="Message Icon">
          <s.RadioButtonContainer>
            {messageIcons.map((icon, index) => (
              <MessageInput key={index}>
                <input
                  type="radio"
                  name="icon"
                  value={icon.value}
                  onChange={props.onChange}
                  checked={icon.value === props.notice.icon}
                  data-testid={`manager-site-notices-add-icon-radio-${icon.value}`}
                />

                {icon.label}
              </MessageInput>
            ))}
          </s.RadioButtonContainer>
        </ModalInput>
        <ModalInput label="Background Color">
          <s.RadioButtonContainer>
            {bgColorIcons.map((icon, idx) => (
              <s.CheckInput key={idx}>
                <input
                  type="radio"
                  name="color"
                  value={icon.label.toUpperCase()}
                  onChange={props.onChange}
                  checked={
                    icon.label.toUpperCase() === props.notice.color ||
                    icon.value.toUpperCase() === props.notice.color
                  }
                  data-testid={`manager-site-notices-add-color-radio-${icon.label}`}
                />

                <Text color={icon.value}>{icon.label}</Text>
              </s.CheckInput>
            ))}
          </s.RadioButtonContainer>
        </ModalInput>
        <ModalInput label="Display for:">
          <s.DisplayWrapper>
            <s.CheckInput>
              <input
                type="radio"
                name="permanent"
                onChange={props.onChangePermanent}
                value={"true"}
                checked={props.isPermanently}
                data-testid="manager-site-notices-add-permanent-radio-true"
              />
              Permanently
            </s.CheckInput>
            <s.CheckInput className="period-option">
              <input
                type="radio"
                name="permanent"
                onChange={props.onChangePermanent}
                value={"false"}
                checked={!props.isPermanently}
                data-testid="manager-site-notices-add-permanent-radio-false"
              />
              Period
              <NumberInput>
                <Input
                  name="period"
                  onChange={props.onChange}
                  value={props.notice.period}
                  disabled={props.isPermanently}
                  data-testid="manager-site-notices-add-period-change"
                />
              </NumberInput>
              Hour(s) when enabled.
            </s.CheckInput>
          </s.DisplayWrapper>
        </ModalInput>
        <s.ButtonWrapper>
          {props.isEdit && (
            <BigButton
              label="Delete"
              buttonType="warning"
              onClickBtn={props.onDelete}
              data-testid="manager-site-notices-add-button-delete"
            />
          )}

          <BigButton
            label="Save"
            onClickBtn={props.onSave}
            data-testid="manager-site-notices-add-button-save"
          />
        </s.ButtonWrapper>
      </s.AddWrapper>
    </BigPopupTemplate>
  );
}
